/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState, useRef } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import dayjs from "dayjs";
import { Else, If, Then } from "react-if";
import {
  Box,
  FlexContainer,
  Spacer,
  theme,
  TimeRangePicker,
  Button,
} from "@nordcloud/gnui";
import { AuditLogIdentityType } from "~/generated/graphql";
import { AppliedFiltersSection } from "~/components/AppliedFiltersSection";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { useAppliedFilters } from "~/hooks/useAppliedFilters";
import { isEmpty, isNotNil } from "~/tools";
import { normalizeDateRange } from "~/utils/dates";
import { useGetAuditLogCounts } from "../hooks/useGetAuditLogCounts/useGetAuditLogCounts";
import { useGetAuditLogs } from "../hooks/useGetAuditLogs/useGetAuditLogs";
import { useGetAuditLogsReport } from "../hooks/useGetAuditLogsReport/useGetAuditLogsReport";
import { getWeekRange, getDefaultTimeRange, quickHash } from "../utils";
import { auditLogsAppliedFilters } from "./auditLogsAppliedFilters";
import { AuditLogsFiltersSidebar } from "./AuditLogsFiltersSidebar";
import { columns } from "./AuditLogsTableColumns";
import { AuditLogsQuery, AuditLogRow } from "./types";

// Preserve selected page when refreshing page, but reset page when selecting different date/time
function useDetectPageRefresh() {
  const refreshHandledRef = useRef(false);

  return (): boolean => {
    if (refreshHandledRef.current) {
      return false;
    }

    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];

    const isRefresh = navEntries[0]?.type === "reload";

    if (isRefresh) {
      refreshHandledRef.current = true;
      return true;
    }

    return false;
  };
}

// eslint-disable-next-line complexity
export function AuditLogsTable() {
  const { queryState, updateQueryState } = useQueryState<AuditLogsQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const appliedFiltersState = useAppliedFilters({
    queryState,
    updateQueryState,
    definitions: auditLogsAppliedFilters,
  });
  // initializes to current week or saved week in query state
  const [weekRange, setWeekRange] = useState(
    getWeekRange(
      queryState?.weekAnchor ? new Date(queryState?.weekAnchor) : new Date()
    )
  );
  const isPageRefresh = useDetectPageRefresh();

  const { data: weeklyLogCounts, loading: weeklyLogCountsLoading } =
    useGetAuditLogCounts({
      timeRangeStart: weekRange.start,
      timeRangeEnd: weekRange.end,
      filters: {
        operation: queryState.operation ?? undefined,
        target: queryState.target ?? undefined,
        identityType:
          (queryState.identityType as AuditLogIdentityType) ?? undefined,
        identity: queryState.identity || undefined,
      },
    });

  const normalizedDateRange = normalizeDateRange(
    queryState.start,
    queryState.end
  );
  const defaultTimeRange = getDefaultTimeRange();

  const { getAuditLogsReport, loading: reportLoading } =
    useGetAuditLogsReport();

  const {
    data: logs,
    count,
    loading: logsLoading,
  } = useGetAuditLogs({
    page: queryState.page ?? 1,
    limit: queryState.limit ?? 20,
    timeRangeStart: normalizedDateRange.start ?? defaultTimeRange.start,
    timeRangeEnd: normalizedDateRange.end ?? defaultTimeRange.end,
    filters: {
      operation: queryState.operation ?? undefined,
      target: queryState.target ?? undefined,
      identityType:
        (queryState.identityType as AuditLogIdentityType) ?? undefined,
      identity: queryState.identity || undefined,
    },
  });

  const data: AuditLogRow[] = logs.map((log) => {
    const fallbackId = `${log.datetime}-${quickHash(log.data)}-${
      log.identity
    }-${log.operation}-${log.target}`;

    const auditLogId = log.tags
      ?.find((tag) => tag.key === "auditLogId")
      ?.value?.trim();

    return {
      ...log,
      id: auditLogId || fallbackId,
      name: `${log.datetime}-${log.identity}-${log.operation}-${log.target}`,
    };
  });

  const onTimeRangeChange = (newTimeRange: {
    start: Date | number;
    end: Date | number;
  }) => {
    const monday = dayjs(newTimeRange.start).startOf("isoWeek");

    updateQueryState({
      ...queryState,
      start: dayjs(newTimeRange.start).toISOString(),
      end: dayjs(newTimeRange.end).subtract(1, "millisecond").toISOString(),
      weekAnchor: monday.toISOString(),
      page: isPageRefresh() ? queryState.page : "1",
    });

    setWeekRange(getWeekRange(monday.toDate()));
  };

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <TimeRangePicker
          initTimeRange={{
            start: normalizedDateRange.start
              ? new Date(normalizedDateRange.start)
              : new Date(defaultTimeRange.start),
            end: normalizedDateRange.end
              ? new Date(normalizedDateRange.end)
              : new Date(defaultTimeRange.end),
          }}
          weekCounts={weeklyLogCounts}
          countsLoading={weeklyLogCountsLoading}
          disabledDays={[{ after: new Date() }]}
          keepSelectedWeekday
          onChange={onTimeRangeChange}
        />
      </Box>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="end">
          <AuditLogsFiltersSidebar resultsCount={count} />
          <Spacer width={theme.spacing.spacing04} />
          <Button
            severity="low"
            icon="download"
            disabled={reportLoading}
            onClick={() => {
              getAuditLogsReport({
                variables: {
                  input: {
                    timeRangeStart:
                      normalizedDateRange.start ?? defaultTimeRange.start,
                    timeRangeEnd:
                      normalizedDateRange.end ?? defaultTimeRange.end,
                    filters: {
                      operation: queryState.operation ?? undefined,
                      target: queryState.target ?? undefined,
                      identityType:
                        (queryState.identityType as AuditLogIdentityType) ??
                        undefined,
                      identity: queryState.identity || undefined,
                    },
                  },
                },
              });
            }}
          >
            Get CSV Report
          </Button>
        </FlexContainer>
        <AppliedFiltersSection state={appliedFiltersState} />
      </Box>
      <LoaderWrap loading={logsLoading || weeklyLogCountsLoading} inContent>
        <If condition={isEmpty(data)}>
          <Then>
            <NoData hasIcon message={"There are no Audit Logs."} />
          </Then>
          <Else>
            <>
              <TableWrapper<Partial<AuditLogRow>>
                loading={logsLoading || weeklyLogCountsLoading}
                data={data.filter(isNotNil)}
                columns={columns()}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
